import { Component } from '@theme/component';

import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/** Where the shopper's coordinates are remembered between visits. */
const LOCATION_STORAGE_KEY = 'theme_store_availability_location';

/** Sort weight for locations with no coordinates, so they fall to the bottom. */
const UNKNOWN_DISTANCE = Number.MAX_SAFE_INTEGER;

const EARTH_RADIUS_KM = 6371;

/** How long to wait on the geolocation prompt before giving up. */
const GEOLOCATION_TIMEOUT = 10000;

const PIN_ICON = `<svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true" focusable="false"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;

/**
 * Great-circle distance between two coordinates, in kilometres.
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number}
 */
function distanceBetween(lat1, lon1, lat2, lon2) {
  const toRadians = (value) => (value * Math.PI) / 180;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Reads the coordinates Liquid rendered onto a location element.
 * @param {HTMLElement} location
 * @returns {{ lat: number, lng: number } | null}
 */
function coordinatesFor(location) {
  const lat = Number.parseFloat(location.dataset.lat ?? '');
  const lng = Number.parseFloat(location.dataset.lng ?? '');

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

/** @returns {{ lat: number, lng: number } | null} */
function getStoredLocation() {
  try {
    const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!stored) return null;

    const { lat, lng } = JSON.parse(stored);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

/**
 * @param {number} lat
 * @param {number} lng
 */
function setStoredLocation(lat, lng) {
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({ lat, lng }));
  } catch {
    // Storage unavailable (private mode, quota) — proximity still works for this page view.
  }
}

function clearStoredLocation() {
  try {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
  } catch {
    // Nothing to do — the value simply persists.
  }
}

/**
 * Renders store stock availability for the selected variant.
 *
 * Locations always sort by availability first. Once the shopper opts into
 * sharing their location, available stores are additionally ordered by
 * distance and gain a directions link.
 */
class StoreAvailability extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {HTMLElement | null} */
  #drawer = null;

  /** Scopes the location-control listeners so they can be replaced after a morph. */
  #controlListeners;

  connectedCallback() {
    super.connectedCallback();

    // Portal the sibling drawer to document.body so its z-index isn't trapped
    // by ancestor stacking contexts on desktop. Mirrors <local-pickup>.
    const drawer =
      this.nextElementSibling instanceof HTMLElement &&
      this.nextElementSibling.matches('theme-drawer.store-availability-drawer')
        ? this.nextElementSibling
        : null;

    if (drawer) {
      this.#drawer = drawer;
      document.body.appendChild(drawer);
    }

    this.#applyStoredLocation();
    this.#bindControls();

    // Re-sort when the drawer opens: another block may have changed the
    // stored location since this one last rendered.
    this.#drawer?.addEventListener('theme-drawer:open', this.#handleDrawerOpen);

    const closestSection = this.closest(`.shopify-section, dialog`);

    /** @type {(event: ProductSelectEvent) => void} */
    const handleProductSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

      event.promise
        .then(({ detail }) => {
          if (!detail) return;

          const { newProduct, resource } = detail;
          if (newProduct) {
            this.dataset.productUrl = newProduct.url;
          }

          const variantId = resource ? resource.id : null;
          if (variantId !== this.dataset.variantId) {
            if (variantId) {
              this.dataset.variantId = variantId;
              this.#fetchAvailability(variantId);
            } else {
              this.setAttribute('hidden', '');
            }
          }
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[store-availability] Event promise rejected:', error);
        });
    };

    closestSection?.addEventListener(StandardEvents.productSelect, handleProductSelect);

    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(StandardEvents.productSelect, handleProductSelect);
      this.#controlListeners?.abort();
      this.#drawer?.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
      this.#drawer?.remove();
      this.#drawer = null;
    };
  }

  #handleDrawerOpen = () => this.#applyStoredLocation();

  /** Elements the location controls live in — the drawer once portaled. */
  get #controlsRoot() {
    return this.#drawer ?? this;
  }

  /** @returns {HTMLElement | null} */
  get #storesContainer() {
    return this.#controlsRoot.querySelector('.store-availability__stores');
  }

  /** @returns {HTMLElement[]} */
  get #locations() {
    return Array.from(this.#controlsRoot.querySelectorAll('.store-availability__location'));
  }

  get #proximityEnabled() {
    return this.dataset.proximityEnabled === 'true';
  }

  get #showDirections() {
    return this.dataset.showDirections === 'true';
  }

  /** Product is tagged to always show the warehouse-only message, regardless of store stock. */
  get #noDisplay() {
    return this.dataset.noDisplay === 'true';
  }

  /**
   * Fills a Liquid-provided label template.
   * @param {string} name - dataset key holding the template.
   * @param {Record<string, string>} tokens
   */
  #label(name, tokens = {}) {
    let template = this.dataset[name] ?? '';

    for (const [token, value] of Object.entries(tokens)) {
      template = template.replaceAll(token, value);
    }

    return template;
  }

  #bindControls() {
    // morph preserves existing nodes, so rebinding after a variant change would
    // stack a second listener on the same button. Drop the previous set first.
    this.#controlListeners?.abort();
    this.#controlListeners = new AbortController();

    const { signal } = this.#controlListeners;
    const root = this.#controlsRoot;

    root.querySelector('.store-availability__request-location')?.addEventListener(
      'click',
      (event) => this.#requestLocation(event.currentTarget),
      { signal }
    );

    root.querySelector('.store-availability__update-location')?.addEventListener(
      'click',
      (event) => this.#requestLocation(event.currentTarget),
      { signal }
    );

    root.querySelector('.store-availability__clear-location')?.addEventListener(
      'click',
      () => {
        clearStoredLocation();
        this.#sortByAvailability();
      },
      { signal }
    );
  }

  #applyStoredLocation() {
    // Server-rendered warehouse-only message; sorting/summary logic below would overwrite it.
    if (this.#noDisplay) return;

    const stored = this.#proximityEnabled ? getStoredLocation() : null;

    if (stored) {
      this.#sortByProximity(stored.lat, stored.lng);
    } else {
      this.#sortByAvailability();
    }
  }

  /** Availability-only ordering, preserving the server-rendered order within each group. */
  #sortByAvailability() {
    const container = this.#storesContainer;
    if (!container) return;

    const locations = this.#locations;

    for (const location of locations) {
      const distanceElement = location.querySelector('.store-availability__distance');
      if (distanceElement instanceof HTMLElement) distanceElement.hidden = true;
      delete location.dataset.distance;
    }

    locations.sort((a, b) => {
      const availability = Number(b.dataset.available === 'true') - Number(a.dataset.available === 'true');
      if (availability !== 0) return availability;

      return Number(a.dataset.originalOrder) - Number(b.dataset.originalOrder);
    });

    for (const location of locations) container.appendChild(location);

    this.#updateSummary(locations, null);
    this.#updateLocationControls(false);
  }

  /**
   * @param {number} userLat
   * @param {number} userLng
   */
  #sortByProximity(userLat, userLng) {
    const container = this.#storesContainer;
    if (!container) return;

    const locations = this.#locations;

    for (const location of locations) {
      const coordinates = coordinatesFor(location);
      const available = location.dataset.available === 'true';
      const distanceElement = location.querySelector('.store-availability__distance');

      if (!coordinates) {
        location.dataset.distance = String(UNKNOWN_DISTANCE);
        if (distanceElement instanceof HTMLElement) distanceElement.hidden = true;
        continue;
      }

      const distance = distanceBetween(userLat, userLng, coordinates.lat, coordinates.lng);
      location.dataset.distance = String(distance);

      // Distance is only actionable for stores that actually have stock.
      if (distanceElement instanceof HTMLAnchorElement && available && this.#showDirections) {
        distanceElement.innerHTML = `${PIN_ICON}<span>${this.#formatDistance(distance)}</span>`;
        distanceElement.href = this.#directionsUrl(userLat, userLng, coordinates);
        distanceElement.hidden = false;
      } else if (distanceElement instanceof HTMLElement) {
        distanceElement.hidden = true;
      }
    }

    locations.sort((a, b) => {
      const availability = Number(b.dataset.available === 'true') - Number(a.dataset.available === 'true');
      if (availability !== 0) return availability;

      return Number(a.dataset.distance ?? UNKNOWN_DISTANCE) - Number(b.dataset.distance ?? UNKNOWN_DISTANCE);
    });

    for (const location of locations) container.appendChild(location);

    this.#updateSummary(locations, { lat: userLat, lng: userLng });
    this.#updateLocationControls(true);
  }

  /**
   * @param {number} distance - kilometres
   */
  #formatDistance(distance) {
    const measurement =
      distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`;

    return this.#label('labelDistanceAway', { __DISTANCE__: measurement });
  }

  /**
   * @param {number} userLat
   * @param {number} userLng
   * @param {{ lat: number, lng: number }} destination
   */
  #directionsUrl(userLat, userLng, destination) {
    return `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${destination.lat},${destination.lng}`;
  }

  /**
   * Rewrites the collapsed summary to describe the nearest available store.
   * @param {HTMLElement[]} sortedLocations
   * @param {{ lat: number, lng: number } | null} userCoordinates
   */
  #updateSummary(sortedLocations, userCoordinates) {
    const summary = this.querySelector('.store-availability__summary');
    if (!summary) return;

    const nearest = sortedLocations.find((location) => location.dataset.available === 'true');
    const icon = this.querySelector('.store-availability__summary-icon');

    if (!nearest) {
      summary.innerHTML = `<p>${this.#label('labelUnavailable')}</p>`;
      icon?.classList.add('store-availability__summary-icon--unavailable');
      return;
    }

    icon?.classList.remove('store-availability__summary-icon--unavailable');

    const displayName = nearest.dataset.displayName ?? '';
    const parts = [`<p>${this.#label('labelAvailableAt', { __LOCATION__: displayName })}</p>`];

    if (nearest.dataset.pickupEnabled === 'true' && nearest.dataset.pickupTime) {
      const pickup = this.#label('labelPickupAvailable', { __TIME__: nearest.dataset.pickupTime });
      parts.push(`<p class="store-availability__meta">${pickup}</p>`);
    }

    const coordinates = coordinatesFor(nearest);
    if (userCoordinates && coordinates && this.#showDirections) {
      const distance = distanceBetween(userCoordinates.lat, userCoordinates.lng, coordinates.lat, coordinates.lng);
      const href = this.#directionsUrl(userCoordinates.lat, userCoordinates.lng, coordinates);

      parts.push(
        `<a class="store-availability__directions" href="${href}" target="_blank" rel="noopener noreferrer">${PIN_ICON}<span>${this.#formatDistance(
          distance
        )}</span></a>`
      );
    }

    summary.innerHTML = parts.join('');
  }

  /** @param {boolean} hasLocation */
  #updateLocationControls(hasLocation) {
    const root = this.#controlsRoot;
    const prompt = root.querySelector('.store-availability__location-prompt');
    const actions = root.querySelector('.store-availability__location-actions');

    if (prompt instanceof HTMLElement) prompt.hidden = hasLocation;
    if (actions instanceof HTMLElement) actions.hidden = !hasLocation;
  }

  /**
   * @param {EventTarget | null} clickedButton
   */
  #requestLocation(clickedButton) {
    if (!navigator.geolocation) return;

    const root = this.#controlsRoot;
    const buttons = [
      clickedButton,
      root.querySelector('.store-availability__request-location'),
      root.querySelector('.store-availability__update-location'),
    ].filter((button) => button instanceof HTMLButtonElement);

    const setLoading = (loading) => {
      for (const button of buttons) {
        button.disabled = loading;

        if (loading) {
          button.dataset.originalText ??= button.textContent?.trim() ?? '';
          button.textContent = this.#label('labelFindingLocation');
        } else if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
        }
      }
    };

    setLoading(true);

    // Safari can leave getCurrentPosition pending indefinitely when the
    // permission prompt is dismissed, so guard the callbacks ourselves.
    let settled = false;
    const settle = () => {
      if (settled) return true;
      settled = true;
      clearTimeout(timeout);
      setLoading(false);
      return false;
    };

    const timeout = setTimeout(() => {
      if (!settle()) this.#sortByAvailability();
    }, GEOLOCATION_TIMEOUT + 1000);

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (settle()) return;
        setStoredLocation(coords.latitude, coords.longitude);
        this.#sortByProximity(coords.latitude, coords.longitude);
      },
      () => {
        if (settle()) return;
        this.#sortByAvailability();
      },
      { enableHighAccuracy: false, timeout: GEOLOCATION_TIMEOUT, maximumAge: 0 }
    );
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Re-renders availability for a newly selected variant.
   * @param {string} variantId
   */
  #fetchAvailability = (variantId) => {
    if (!variantId) return;

    const abortController = this.#createAbortController();
    const url = this.dataset.productUrl;

    fetch(`${url}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        // Scope by block id so sibling blocks on the same page don't collide.
        const wrapper = html.querySelector(`store-availability[data-block-id="${this.dataset.blockId}"]`);

        if (!wrapper) {
          this.setAttribute('hidden', '');
          return;
        }

        this.removeAttribute('hidden');
        morph(this, wrapper);

        const newDrawer =
          wrapper.nextElementSibling instanceof HTMLElement &&
          wrapper.nextElementSibling.matches('theme-drawer.store-availability-drawer')
            ? wrapper.nextElementSibling
            : null;

        if (newDrawer) {
          if (this.#drawer) {
            morph(this.#drawer, newDrawer);
          } else {
            this.#drawer = newDrawer;
            document.body.appendChild(this.#drawer);
            this.#drawer.addEventListener('theme-drawer:open', this.#handleDrawerOpen);
          }
        }

        // Fresh markup means fresh listeners and a fresh sort.
        this.#bindControls();
        this.#applyStoredLocation();
      })
      .catch(() => {
        if (abortController.signal.aborted) return;
        this.setAttribute('hidden', '');
      });
  };
}

if (!customElements.get('store-availability')) {
  customElements.define('store-availability', StoreAvailability);
}
